import React, { useEffect, useMemo, useState } from 'react'
import AlxModalShell from './weldUpload/AlxModalShell'
import Button from '../ui/Button'
import SingleSelect from '../ui/SingleSelect'
import {
  GRAPH_TILT_LINE_BREAK_DELTA,
  TILT_GAP_BREAK_OPTIONS,
  formatTiltGapBreakLabel,
  normalizeTiltGapBreakDelta
} from '../../utils/passAnalyticsGraphAxis'

export interface TiltGapBreakDialogProps {
  open: boolean
  onClose: () => void
  /** Current project value (defaults to 90). */
  value: number
  onSave: (next: number) => void | Promise<void>
  busy?: boolean
}

/**
 * Project-scoped Pass Analytics setting: break the graph line when consecutive
 * unwrapped tilt points jump more than the selected degrees.
 */
export default function TiltGapBreakDialog({
  open,
  onClose,
  value,
  onSave,
  busy = false
}: TiltGapBreakDialogProps) {
  const optionValues = useMemo(
    () => TILT_GAP_BREAK_OPTIONS.map((n) => String(n)),
    []
  )
  const [draft, setDraft] = useState(String(normalizeTiltGapBreakDelta(value)))

  useEffect(() => {
    if (!open) return
    setDraft(String(normalizeTiltGapBreakDelta(value)))
  }, [open, value])

  const handleSave = async () => {
    const next = normalizeTiltGapBreakDelta(Number(draft))
    await onSave(next)
  }

  return (
    <AlxModalShell
      open={open}
      onClose={busy ? () => undefined : onClose}
      title="Tilt gap break"
      subtitle="Break the graph line when consecutive tilt points jump more than this many degrees. Choose No break to keep a continuous line (previous behavior)."
      width="md"
      testId="tilt-gap-break-dialog"
      titleId="tilt-gap-break-title"
      closeDisabled={busy}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            data-testid="tilt-gap-break-cancel"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="tilt-gap-break-save"
            onClick={() => void handleSave()}
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <label className="mb-2 block text-xs font-semibold text-heritage-grey" htmlFor="tilt-gap-break-select">
        Gap threshold
      </label>
      <SingleSelect
        value={draft || String(GRAPH_TILT_LINE_BREAK_DELTA)}
        options={optionValues}
        onChange={setDraft}
        getOptionLabel={(v) => formatTiltGapBreakLabel(Number(v))}
        getTriggerLabel={(v) => formatTiltGapBreakLabel(Number(v))}
        placeholder="Select threshold"
        searchPlaceholder="Search…"
        dropdownPortal
      />
      <p className="mt-3 text-xs text-heritage-grey">
        Default is {GRAPH_TILT_LINE_BREAK_DELTA}°. Applies to all Pass Analytics graphs for this
        project.
      </p>
    </AlxModalShell>
  )
}
